import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Car, Clock, MapPin, Star, Sparkles, Shield, Zap, Users, ArrowRight, CheckCircle, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

export const TimelyHero = () => {
  return (
    <div className="relative min-h-[90vh] sm:min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-100/20 to-indigo-100/20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-200/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>
      
      <div className="relative z-10 container mx-auto px-4 py-12 sm:py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-6 sm:space-y-8">
            <div className="space-y-4 sm:space-y-6">
              <Badge variant="secondary" className="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border-blue-200 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium inline-flex items-center max-w-full">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                <span className="break-words">India's Leading Vehicle Wash Platform</span>
              </Badge>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight break-words">
                Book Your
                <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent"> Vehicle Wash</span>
                <br />
                in Minutes
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl leading-relaxed break-words">
                Connect with premium wash centers, get real-time updates via WhatsApp, and enjoy hassle-free vehicle care with India's most trusted platform.
              </p>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:gap-6 pt-2 sm:pt-4">
              <div className="flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm text-gray-600">
                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                <span>100% Secure</span>
              </div>
              <div className="flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm text-gray-600">
                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                <span>WhatsApp Integration</span>
              </div>
              <div className="flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm text-gray-600">
                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                <span>Real-time Updates</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 max-w-2xl">
              <Link to="/booking" className="w-full md:flex-1">
                <Button size="lg" className="w-full h-auto min-h-[52px] bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group px-6 sm:px-8 py-3.5 flex items-center justify-center gap-2">
                  <Car className="w-5 h-5 group-hover:animate-pulse flex-shrink-0" />
                  <span className="whitespace-nowrap">Book Now</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </Button>
              </Link>
              <Link to="/onboard-vendor" className="w-full md:flex-1">
                <Button size="lg" variant="outline" className="w-full h-auto min-h-[52px] border-2 border-blue-300 text-blue-700 hover:bg-blue-50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-6 sm:px-8 py-3.5 flex items-center justify-center gap-2">
                  <Users className="w-5 h-5 flex-shrink-0" />
                  <span className="whitespace-nowrap">Join as Vendor</span>
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8 pt-6 sm:pt-8 border-t border-gray-200">
              <div className="text-center group">
                <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-blue-600 group-hover:scale-110 transition-transform duration-300 break-words">10K+</div>
                <div className="text-xs sm:text-sm text-gray-600 mt-1 break-words leading-tight">Happy Customers</div>
              </div>
              <div className="text-center group">
                <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-indigo-600 group-hover:scale-110 transition-transform duration-300 break-words">500+</div>
                <div className="text-xs sm:text-sm text-gray-600 mt-1 break-words leading-tight">Wash Centers</div>
              </div>
              <div className="text-center group">
                <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-purple-600 group-hover:scale-110 transition-transform duration-300 break-words">50K+</div>
                <div className="text-xs sm:text-sm text-gray-600 mt-1 break-words leading-tight">Washes Done</div>
              </div>
            </div>
          </div>

          {/* Right Content - Modern Visual */}
          <div className="relative hidden lg:block">
            <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-6 lg:p-8 border border-white/20">
              <div className="space-y-6">
                {/* Mock Phone Interface */}
                <div className="bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 rounded-2xl p-6 text-white shadow-xl">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-white/40 rounded-full"></div>
                      <div className="w-3 h-3 bg-white/40 rounded-full"></div>
                      <div className="w-3 h-3 bg-white/40 rounded-full"></div>
                    </div>
                    <div className="text-sm font-semibold">Timelyyy</div>
                    <div className="w-6 h-6 bg-white/30 rounded-lg flex items-center justify-center">
                      <Car className="w-4 h-4" />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 hover:bg-white/30 transition-all duration-300">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center">
                          <MapPin className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="text-sm font-medium">Find nearby wash centers</div>
                          <div className="text-xs text-white/80">Real-time location services</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 hover:bg-white/30 transition-all duration-300">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center">
                          <Clock className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="text-sm font-medium">Book your slot</div>
                          <div className="text-xs text-white/80">Flexible scheduling</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 hover:bg-white/30 transition-all duration-300">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center">
                          <MessageCircle className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="text-sm font-medium">WhatsApp updates</div>
                          <div className="text-xs text-white/80">Instant notifications</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feature Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-4 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-lg transition-all duration-300 group">
                    <CardContent className="p-0">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <Shield className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-blue-800">Secure Payments</div>
                          <div className="text-xs text-blue-600">Multiple options</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="p-4 border-green-200 bg-gradient-to-br from-green-50 to-green-100 hover:shadow-lg transition-all duration-300 group">
                    <CardContent className="p-0">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <Star className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-green-800">4.8 Rating</div>
                          <div className="text-xs text-green-600">Trusted by users</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-blue-400 to-indigo-400 rounded-full flex items-center justify-center animate-bounce shadow-xl">
              <Zap className="w-10 h-10 text-white" />
            </div>
            <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center animate-pulse shadow-xl">
              <Car className="w-8 h-8 text-white" />
            </div>
            <div className="absolute top-1/2 -left-8 w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center animate-ping shadow-lg">
              <Star className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
